import type { ReportResponseDto } from '@api/WhisApi/generated';
import { ReportResponseDtoStatus } from '@api/WhisApi/generated';
import { UserBodyDtoRole } from '@api/WhisApi/generated/model/userBodyDtoRole';
import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { TabItem } from '@components/NavBar/navBar.types';

/**
 * Determines whether a user can access/see a given navigation tab.
 *
 * Resolution order:
 * 1. `hiddenFrom` (deny-list) takes priority — a listed role is always denied.
 * 2. `visibleTo` (allow-list) — if set, only listed roles are allowed.
 * 3. Otherwise, everyone is allowed.
 */
export const canAccessTab = (
  tab: TabItem,
  user?: UserDto | null,
): boolean => {
  const role = user?.role as UserBodyDtoRole | undefined;

  if (tab.hiddenFrom?.length && role && tab.hiddenFrom.includes(role)) {
    return false;
  }

  if (tab.visibleTo?.length) {
    return Boolean(role && tab.visibleTo.includes(role));
  }

  return true;
};

export const isUserAdministrator = (user?: UserDto | null): boolean =>
  user?.role === UserBodyDtoRole.admin;

export const isUserDelegate = (user?: UserDto | null): boolean =>
  user?.role === UserBodyDtoRole.delegate;

export const canManageUsers = (user?: UserDto | null): boolean =>
  Boolean(
    user &&
    (user.role === UserBodyDtoRole.admin ||
      user.role === UserBodyDtoRole.delegate),
  );

const isReportAuthor = (
  report?: ReportResponseDto | null,
  user?: UserDto | null,
): boolean => Boolean(report && user && report.user.id === user.id);

const isDelegateInReportCountry = (
  report?: ReportResponseDto | null,
  user?: UserDto | null,
): boolean =>
  Boolean(
    report &&
    user &&
    isUserDelegate(user) &&
    user.country?.code === report.country.code,
  );

export const canEditReport = (
  report?: ReportResponseDto | null,
  user?: UserDto | null,
): boolean =>
  Boolean(
    report &&
    user &&
    (isReportAuthor(report, user) ||
      isUserAdministrator(user) ||
      isDelegateInReportCountry(report, user)),
  );

export const canDeleteReportDraft = (
  report?: ReportResponseDto | null,
  user?: UserDto | null,
): boolean =>
  Boolean(
    report &&
    user &&
    report.status === ReportResponseDtoStatus.draft &&
    (isReportAuthor(report, user) ||
      isUserAdministrator(user) ||
      isDelegateInReportCountry(report, user)),
  );
